import React, { useState, useEffect, useCallback } from 'react';
import { Search, Eye, Send, CreditCard, X, Loader, Printer, FileText, CheckCircle } from 'lucide-react';
import api from '../../utils/api';
import { useSettings } from '../../context/SettingsContext';

const SC = {
  paid:    'bg-green-100 text-green-800',
  sent:    'bg-blue-100 text-blue-800',
  unpaid:  'bg-red-100 text-red-800',
  overdue: 'bg-orange-100 text-orange-800',
};

const TABS = [
  { id: 'towing',       label: 'Towing Invoices',     icon: '🚛' },
  { id: 'b2b',          label: 'B2B Client Invoices',  icon: '🏢' },
  { id: 'car_purchase', label: 'Car Purchase Invoices', icon: '🚗' },
];

export default function Invoices() {
  const { settings } = useSettings();
  const [invoices, setInvoices] = useState([]);
  const [tab, setTab]           = useState('towing');
  const [search, setSearch]     = useState('');
  const [loading, setLoading]   = useState(true);
  const [viewing, setViewing]   = useState(null);
  const [toast, setToast]       = useState('');

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3000); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/api/invoices?invoice_type=${tab}${search ? `&search=${search}` : ''}`);
      setInvoices(res.data);
    } catch(e) {} finally { setLoading(false); }
  }, [tab, search]);

  useEffect(() => { load(); }, [load]);

  const togglePaid = async (id, status) => {
    await api.put(`/api/invoices/${id}`, { status: status === 'paid' ? 'sent' : 'paid' });
    showToast(status === 'paid' ? 'Marked as unpaid' : '✅ Marked as paid!');
    load();
  };

  // ── Professional print template — pulls everything from Settings ──
  const printInv = (inv) => {
    const co   = settings;
    const amt  = inv.amount || 0;
    const gst  = Math.round(amt / 11);
    const exGst = amt - gst;
    const win  = window.open('', '_blank', 'width=960,height=800');

    win.document.write(`<!DOCTYPE html>
<html>
<head>
  <title>${inv.number}</title>
  <style>
    *{box-sizing:border-box;margin:0;padding:0}
    body{font-family:Arial,Helvetica,sans-serif;padding:44px;color:#1a1a2e;font-size:13px;line-height:1.5}
    .header{display:flex;justify-content:space-between;align-items:flex-start;padding-bottom:20px;margin-bottom:24px;border-bottom:3px solid #0D2B6B}
    .co-name{font-size:22px;font-weight:900;color:#0D2B6B;letter-spacing:-0.5px}
    .co-sub{font-size:10px;color:#aaa;text-transform:uppercase;letter-spacing:2px;margin-top:2px}
    .co-contact{font-size:12px;color:#555;margin-top:8px;line-height:1.8}
    .inv-title{font-size:11px;font-weight:700;text-transform:uppercase;color:#aaa;letter-spacing:2px;margin-bottom:4px}
    .inv-number{font-size:22px;font-weight:900;color:#0D2B6B}
    .two-col{display:grid;grid-template-columns:1fr 1fr;gap:16px;margin-bottom:20px}
    .box{background:#f8f9fc;padding:14px;border-radius:8px}
    .box-label{font-size:10px;font-weight:700;text-transform:uppercase;color:#aaa;margin-bottom:6px;letter-spacing:1px}
    .box-name{font-size:15px;font-weight:700;color:#1a1a2e}
    .box-detail{font-size:12px;color:#666;margin-top:2px}
    table{width:100%;border-collapse:collapse;margin:8px 0}
    thead th{background:#f1f4fb;text-align:left;padding:10px 12px;font-size:11px;color:#666;text-transform:uppercase;letter-spacing:.5px;font-weight:700}
    thead th:last-child{text-align:right}
    tbody td{padding:10px 12px;border-bottom:1px solid #eee;font-size:13px}
    tbody td:last-child{text-align:right;font-weight:600}
    .total-row td{background:#0D2B6B;color:#fff;font-weight:900;font-size:16px;padding:14px 12px}
    .total-row td:last-child{text-align:right}
    .gst-line{font-size:12px;color:#888;padding:8px 0 16px;border-bottom:1px solid #eee;margin-bottom:16px}
    .pay-box{background:#eef2ff;border:1px solid #c7d2fe;border-radius:8px;padding:16px;margin-top:16px}
    .pay-title{font-size:12px;font-weight:700;color:#0D2B6B;margin-bottom:8px;text-transform:uppercase;letter-spacing:.5px}
    .pay-row{font-size:12px;color:#444;margin-bottom:3px}
    .pay-row strong{color:#1a1a2e}
    .terms{font-size:11px;color:#888;margin-top:8px;font-style:italic}
    .sig-section{margin-top:28px;padding-top:16px;border-top:1px dashed #ddd}
    .sig-label{font-size:10px;font-weight:700;text-transform:uppercase;color:#aaa;letter-spacing:1px;margin-bottom:8px}
    .sig-img{height:70px;display:block;border-bottom:2px solid #0D2B6B;padding-bottom:4px;max-width:300px}
    .sig-method{font-size:11px;color:#888;margin-top:6px}
    .status-badge{display:inline-block;padding:3px 12px;border-radius:20px;font-size:11px;font-weight:700;margin-top:10px}
    .paid-badge{background:#d1fae5;color:#065f46}
    .unpaid-badge{background:#fee2e2;color:#991b1b}
    .sent-badge{background:#dbeafe;color:#1e40af}
    .footer{margin-top:36px;padding-top:16px;border-top:1px solid #eee;text-align:center;font-size:11px;color:#aaa}
    @media print{body{print-color-adjust:exact;-webkit-print-color-adjust:exact}}
  </style>
</head>
<body>

  <!-- HEADER -->
  <div class="header">
    <div>
      ${co.logo_url ? `<img src="${co.logo_url}" style="height:52px;object-fit:contain;margin-bottom:10px;display:block" onerror="this.style.display='none'" />` : ''}
      <div class="co-name">${co.company_name || 'APEX TOWING SOLUTIONS'}</div>
      <div class="co-sub">& Equipment Transport</div>
      <div class="co-contact">
        ${co.company_phone ? `📞 ${co.company_phone}<br/>` : ''}
        ${co.company_email ? `✉ ${co.company_email}<br/>` : ''}
        ${co.company_address ? `📍 ${co.company_address}<br/>` : ''}
        ${co.company_abn ? `ABN: ${co.company_abn}` : ''}
      </div>
    </div>
    <div style="text-align:right">
      <div class="inv-title">Tax Invoice</div>
      <div class="inv-number">${inv.number}</div>
      <div style="font-size:12px;color:#888;margin-top:8px">
        Date: <strong>${inv.created_at?.split('T')[0] || '—'}</strong><br/>
        Due: <strong>${inv.due_date || 'On receipt'}</strong>
      </div>
      <div>
        <span class="status-badge ${inv.status==='paid' ? 'paid-badge' : inv.status==='sent' ? 'sent-badge' : 'unpaid-badge'}">
          ${(inv.status || 'unpaid').toUpperCase()}
        </span>
      </div>
    </div>
  </div>

  <!-- BILL TO + JOB DETAILS -->
  <div class="two-col">
    <div class="box">
      <div class="box-label">Bill To</div>
      <div class="box-name">${inv.client_name || '—'}</div>
      ${inv.client_email ? `<div class="box-detail">${inv.client_email}</div>` : ''}
    </div>
    ${(inv.vehicle?.pickup || inv.vehicle?.vehicle_rego) ? `
    <div class="box">
      <div class="box-label">Job Details</div>
      ${inv.vehicle?.pickup ? `<div class="box-detail">📍 From: ${inv.vehicle.pickup} ${inv.vehicle.pickup_suburb||''}</div>` : ''}
      ${inv.vehicle?.dropoff ? `<div class="box-detail">📍 To: ${inv.vehicle.dropoff} ${inv.vehicle.dropoff_suburb||''}</div>` : ''}
      ${inv.vehicle?.vehicle_rego ? `<div class="box-detail" style="margin-top:4px">🚗 ${inv.vehicle.vehicle_year||''} ${inv.vehicle.vehicle_make||''} ${inv.vehicle.vehicle_model||''}</div>` : ''}
      ${inv.vehicle?.vehicle_rego ? `<div class="box-detail">Rego: <strong>${inv.vehicle.vehicle_rego}</strong></div>` : ''}
      ${inv.vehicle?.vehicle_condition ? `<div class="box-detail">Condition: ${inv.vehicle.vehicle_condition.replace('_',' ')}</div>` : ''}
    </div>` : '<div></div>'}
  </div>

  <!-- LINE ITEMS TABLE -->
  <table>
    <thead>
      <tr>
        <th>Description</th>
        <th style="text-align:right">Amount (incl. GST)</th>
      </tr>
    </thead>
    <tbody>
      ${(inv.items||[]).map(i => `
        <tr>
          <td>${i.desc || '—'}</td>
          <td>$${((i.total||i.rate||0)).toLocaleString()}</td>
        </tr>`).join('')}
    </tbody>
    <tfoot>
      <tr class="total-row">
        <td>TOTAL (incl. GST)</td>
        <td>$${amt.toLocaleString()}</td>
      </tr>
    </tfoot>
  </table>

  <!-- GST BREAKDOWN -->
  <div class="gst-line">
    GST included: <strong>$${gst.toLocaleString()}</strong>
    &nbsp;·&nbsp;
    Ex-GST: <strong>$${exGst.toLocaleString()}</strong>
  </div>

  <!-- PAYMENT DETAILS -->
  ${(co.bsb || co.payid || co.account_number) ? `
  <div class="pay-box">
    <div class="pay-title">💳 How to Pay</div>
    ${co.bsb ? `<div class="pay-row">Bank Transfer — BSB: <strong>${co.bsb}</strong> · Account: <strong>${co.account_number}</strong> · Name: <strong>${co.account_name||''}</strong></div>` : ''}
    ${co.payid ? `<div class="pay-row">PayID: <strong>${co.payid}</strong></div>` : ''}
    ${inv.payment_method ? `<div class="pay-row">Payment recorded: <strong style="text-transform:capitalize">${inv.payment_method.replace('_',' ')}</strong></div>` : ''}
    ${co.payment_terms ? `<div class="terms">${co.payment_terms}</div>` : ''}
  </div>` : ''}

  <!-- SIGNATURE -->
  ${inv.signature ? `
  <div class="sig-section">
    <div class="sig-label">Customer Signature</div>
    <img src="${inv.signature}" class="sig-img" />
    <div class="sig-method">
      Signed on ${inv.created_at?.split('T')[0] || '—'}
      ${inv.payment_method ? ` · Payment: ${inv.payment_method.replace('_',' ')}` : ''}
    </div>
  </div>` : ''}

  <!-- FOOTER NOTE -->
  ${co.invoice_notes ? `<div class="footer">${co.invoice_notes}</div>` : ''}

</body>
</html>`);

    win.document.close();
    setTimeout(() => win.print(), 600);
  };

  const total   = invoices.reduce((s, i) => s + (i.amount || 0), 0);
  const paidAmt = invoices.filter(i => i.status==='paid').reduce((s, i) => s + (i.amount||0), 0);
  const unpaid  = total - paidAmt;

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto">
      {/* Toast */}
      {toast && (
        <div className="fixed bottom-6 right-6 bg-apex-blue-700 text-white px-5 py-3 rounded-xl font-semibold text-sm shadow-xl z-50 flex items-center gap-2">
          <CheckCircle className="w-4 h-4" />{toast}
        </div>
      )}

      {/* Header */}
      <div className="mb-5">
        <h1 className="text-2xl font-black text-apex-blue-700">Invoices</h1>
        <p className="text-sm text-gray-400 mt-0.5">All invoices generated from bookings and B2B jobs</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-4 flex-wrap">
        {TABS.map(({ id, label, icon }) => (
          <button key={id} onClick={() => setTab(id)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold border-2 transition-all
              ${tab===id ? 'bg-apex-blue-700 border-apex-blue-700 text-white' : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'}`}>
            <span>{icon}</span>{label}
          </button>
        ))}
      </div>

      {/* Search + Summary */}
      <div className="flex gap-3 mb-4 flex-wrap items-center">
        <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-xl px-4 py-2.5 flex-1 min-w-52">
          <Search className="w-4 h-4 text-gray-300 shrink-0" />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Client name, invoice number..." className="outline-none text-sm flex-1" />
        </div>
        {invoices.length > 0 && (
          <div className="flex gap-3">
            <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-2 text-center">
              <p className="text-[10px] font-bold text-green-600 uppercase">Paid</p>
              <p className="font-black text-green-700">${paidAmt.toLocaleString()}</p>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-2 text-center">
              <p className="text-[10px] font-bold text-red-500 uppercase">Outstanding</p>
              <p className="font-black text-red-600">${unpaid.toLocaleString()}</p>
            </div>
            <div className="bg-apex-blue-50 border border-apex-blue-200 rounded-xl px-4 py-2 text-center">
              <p className="text-[10px] font-bold text-apex-blue-600 uppercase">Total</p>
              <p className="font-black text-apex-blue-700">${total.toLocaleString()}</p>
            </div>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-16"><Loader className="w-8 h-8 text-apex-blue-500 animate-spin" /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[650px]">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  {['Invoice #','Client','Date','Amount','GST','Status','Payment','Actions'].map(h => (
                    <th key={h} className="text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wider whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {invoices.length === 0 && (
                  <tr><td colSpan={8} className="text-center py-16 text-gray-400">
                    <FileText className="w-10 h-10 mx-auto mb-3 text-gray-200" />
                    <p className="font-medium">No {TABS.find(t=>t.id===tab)?.label.toLowerCase()} yet</p>
                  </td></tr>
                )}
                {invoices.map(inv => (
                  <tr key={inv.id} className="hover:bg-gray-50 transition-colors cursor-pointer" onClick={() => setViewing(inv)}>
                    <td className="px-4 py-3.5 font-mono text-xs font-bold text-apex-blue-700 whitespace-nowrap">{inv.number}</td>
                    <td className="px-4 py-3.5">
                      <p className="font-bold text-gray-900">{inv.client_name}</p>
                      <p className="text-xs text-gray-400 mt-0.5">{inv.client_email || '—'}</p>
                    </td>
                    <td className="px-4 py-3.5">
                      <p className="font-medium text-gray-800">{inv.created_at?.split('T')[0]}</p>
                      <p className="text-xs text-gray-400">{inv.due_date ? `Due: ${inv.due_date}` : 'Due on receipt'}</p>
                    </td>
                    <td className="px-4 py-3.5 font-black text-gray-900 text-base whitespace-nowrap">
                      ${(inv.amount||0).toLocaleString()}
                    </td>
                    <td className="px-4 py-3.5 text-sm text-gray-500 whitespace-nowrap">
                      ${Math.round((inv.amount||0)/11).toLocaleString()}
                    </td>
                    <td className="px-4 py-3.5">
                      <span className={`text-xs font-bold px-2.5 py-1 rounded-full capitalize ${SC[inv.status]||'bg-gray-100 text-gray-600'}`}>
                        {inv.status || 'unpaid'}
                      </span>
                      {inv.signature && <span className="ml-1.5 text-xs text-green-600" title="Has signature">✍️</span>}
                    </td>
                    <td className="px-4 py-3.5 text-sm text-gray-600 capitalize">
                      {inv.payment_method?.replace('_',' ') || '—'}
                    </td>
                    <td className="px-4 py-3.5" onClick={e => e.stopPropagation()}>
                      <div className="flex gap-1.5">
                        <button onClick={() => setViewing(inv)} title="View Invoice"
                          className="p-2 rounded-xl border border-gray-200 hover:bg-blue-50 text-apex-blue-500">
                          <Eye className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => printInv(inv)} title="Print Invoice"
                          className="p-2 rounded-xl border border-gray-200 hover:bg-gray-50 text-gray-600">
                          <Printer className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={async () => {
                          if (!inv.client_email) { alert('No email address on this invoice.'); return; }
                          try {
                            await api.post(`/api/invoices/${inv.id}/send-email`);
                            showToast(`✅ Invoice emailed to ${inv.client_email}`);
                          } catch(e) {
                            alert(e.response?.data?.detail || 'Email failed. Check SMTP settings in Admin → Settings.');
                          }
                        }} title="Email Invoice"
                          className="p-2 rounded-xl border border-green-200 hover:bg-green-50 text-green-600">
                          <Send className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => togglePaid(inv.id, inv.status)} title="Toggle Paid"
                          className={`p-2 rounded-xl border transition-all ${inv.status==='paid'
                            ? 'border-green-200 bg-green-50 text-green-600'
                            : 'border-amber-200 hover:bg-amber-50 text-amber-600'}`}>
                          <CreditCard className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
              {invoices.length > 0 && (
                <tfoot>
                  <tr className="bg-apex-blue-700">
                    <td colSpan={3} className="px-4 py-3 font-bold text-white">Total ({invoices.length} invoices)</td>
                    <td className="px-4 py-3 font-black text-apex-yellow-500 text-lg">${total.toLocaleString()}</td>
                    <td className="px-4 py-3 text-white/50 text-sm">${Math.round(total/11).toLocaleString()} GST</td>
                    <td colSpan={3} />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}
      </div>

      {/* ── View Invoice Modal ── */}
      {viewing && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={e => { if(e.target===e.currentTarget) setViewing(null); }}>
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[92vh] flex flex-col shadow-2xl">

            {/* Modal header */}
            <div className="flex items-center justify-between px-6 py-4 border-b shrink-0">
              <div>
                <h2 className="font-black text-apex-blue-700 text-lg">{viewing.number}</h2>
                <p className="text-xs text-gray-400 mt-0.5">
                  {viewing.created_at?.split('T')[0]} · Due: {viewing.due_date || 'On receipt'}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-xs font-bold px-3 py-1 rounded-full capitalize ${SC[viewing.status]||'bg-gray-100 text-gray-600'}`}>
                  {viewing.status || 'unpaid'}
                </span>
                <button onClick={() => setViewing(null)} className="p-1.5 rounded-lg hover:bg-gray-100">
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              </div>
            </div>

            <div className="overflow-y-auto flex-1 p-6 space-y-4">
              {/* Company + Invoice number */}
              <div className="flex justify-between items-start">
                <div className="flex items-center gap-3">
                  {settings.logo_url && (
                    <img src={settings.logo_url} alt="logo" className="h-10 object-contain"
                      onError={e => e.target.style.display='none'} />
                  )}
                  <div>
                    <p className="font-black text-apex-blue-700 text-base">{settings.company_name || 'APEX TOWING SOLUTIONS'}</p>
                    <p className="text-xs text-gray-400">{settings.company_phone} · {settings.company_email}</p>
                    {settings.company_abn && <p className="text-xs text-gray-400">ABN: {settings.company_abn}</p>}
                  </div>
                </div>
              </div>

              {/* Bill To */}
              <div className="bg-gray-50 rounded-xl p-4">
                <p className="text-[10px] font-bold text-gray-400 uppercase mb-1.5">Bill To</p>
                <p className="font-bold text-gray-900 text-base">{viewing.client_name}</p>
                {viewing.client_email && <p className="text-sm text-gray-500">{viewing.client_email}</p>}
              </div>

              {/* Vehicle / Job Details */}
              {(viewing.vehicle?.pickup || viewing.vehicle?.vehicle_rego) && (
                <div className="bg-blue-50 rounded-xl p-4">
                  <p className="text-[10px] font-bold text-apex-blue-700 uppercase mb-1.5">Job Details</p>
                  {viewing.vehicle?.pickup && <p className="text-sm text-gray-700">📍 {viewing.vehicle.pickup} → {viewing.vehicle.dropoff}</p>}
                  {viewing.vehicle?.vehicle_rego && (
                    <p className="text-sm text-gray-700 mt-1">
                      🚗 {viewing.vehicle.vehicle_year} {viewing.vehicle.vehicle_make} {viewing.vehicle.vehicle_model} · {viewing.vehicle.vehicle_rego}
                    </p>
                  )}
                </div>
              )}

              {/* Line items */}
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="text-left p-3 text-xs text-gray-400 uppercase font-bold rounded-tl-xl">Description</th>
                    <th className="text-right p-3 text-xs text-gray-400 uppercase font-bold rounded-tr-xl">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {(viewing.items||[]).map((it,i) => (
                    <tr key={i} className="border-t border-gray-100">
                      <td className="p-3 text-gray-700">{it.desc}</td>
                      <td className="p-3 text-right font-semibold">${(it.total||it.rate||0).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-apex-blue-700 text-white">
                    <td className="p-3 font-bold rounded-bl-xl">TOTAL (incl. GST)</td>
                    <td className="p-3 text-right font-black text-xl rounded-br-xl">${(viewing.amount||0).toLocaleString()}</td>
                  </tr>
                </tfoot>
              </table>

              {/* GST note */}
              <p className="text-xs text-gray-400">
                GST included: <strong>${Math.round((viewing.amount||0)/11).toLocaleString()}</strong>
                &nbsp;·&nbsp;
                Ex-GST: <strong>${((viewing.amount||0) - Math.round((viewing.amount||0)/11)).toLocaleString()}</strong>
              </p>

              {/* Banking */}
              {(settings.bsb || settings.payid) && (
                <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-4">
                  <p className="text-xs font-bold text-indigo-700 uppercase tracking-wide mb-2">Payment Details</p>
                  {settings.bsb && <p className="text-xs text-gray-700">Bank: <strong>BSB {settings.bsb}</strong> · Acc <strong>{settings.account_number}</strong> · {settings.account_name}</p>}
                  {settings.payid && <p className="text-xs text-gray-700 mt-1">PayID: <strong>{settings.payid}</strong></p>}
                  {viewing.payment_method && <p className="text-xs text-gray-600 mt-1.5">Recorded payment: <strong className="capitalize">{viewing.payment_method.replace('_',' ')}</strong></p>}
                </div>
              )}

              {/* Signature */}
              {viewing.signature && (
                <div className="border-t border-gray-100 pt-4">
                  <p className="text-[10px] font-bold text-gray-400 uppercase mb-2">Customer Signature</p>
                  <img src={viewing.signature} alt="Signature"
                    className="h-16 border-b-2 border-apex-blue-700 pb-1 max-w-[280px]" />
                  <p className="text-xs text-green-600 mt-1.5 font-medium">✍️ Signed by customer</p>
                </div>
              )}

              {/* Terms */}
              {settings.payment_terms && (
                <p className="text-xs text-gray-400 italic">{settings.payment_terms}</p>
              )}
              {settings.invoice_notes && (
                <p className="text-xs text-gray-400 text-center border-t border-gray-100 pt-3">{settings.invoice_notes}</p>
              )}
            </div>

            {/* Modal actions */}
            <div className="border-t border-gray-100 p-4 flex gap-3 shrink-0">
              <button onClick={() => printInv(viewing)}
                className="flex-1 bg-apex-blue-700 text-white py-3 rounded-xl font-bold flex items-center justify-center gap-2 hover:bg-apex-blue-600">
                <Printer className="w-4 h-4" /> Print Invoice
              </button>
              <button onClick={() => togglePaid(viewing.id, viewing.status)}
                className={`flex-1 py-3 rounded-xl font-bold flex items-center justify-center gap-2 border-2 transition-all
                  ${viewing.status==='paid'
                    ? 'border-gray-200 text-gray-600 hover:bg-gray-50'
                    : 'border-green-300 bg-green-50 text-green-700 hover:bg-green-100'}`}>
                <CreditCard className="w-4 h-4" />
                {viewing.status==='paid' ? 'Mark Unpaid' : 'Mark as Paid'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}